import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    DataApproverAccessResponse,
    DataApproverAccessSearchResponse,
    DataApproverRequest,
    DataApproverUsersSearchResponse,
    DataApproverVesselGroupsSearchResponse,
    UpsertDataApproverAccessRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/crewAndShoreDataApprover.api.ts';
import {
    createApiDataApproverAccess,
    deleteApiDataApproverAccess,
    getApiDataApproverAccess,
    getApiDataApproverUsers,
    getApiDataApproverVesselGroups,
    updateApiDataApproverAccess,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/crewAndShoreDataApprover.api.ts';
import { extractError } from '@utils/slice.ts';

interface ShoreDataApproverError {
    message?: string;
    [key: string]: any;
}

interface ShoreDataApproverState {
    error: ShoreDataApproverError | null;
    shoreDataApproversData: DataApproverAccessSearchResponse | null;
    shoreDataApproverUsersData: DataApproverUsersSearchResponse | null;
    shoreDataApproverVesselGroupsData: DataApproverVesselGroupsSearchResponse | null;

    isGetShoreDataApproversPending: boolean;
    isCreateShoreDataApproverPending: boolean;
    isUpdateShoreDataApproverPending: boolean;
    isDeleteShoreDataApproverPending: boolean;
    isGetShoreDataApproverUsersPending: boolean;
    isGetShoreDataApproverVesselGroupsPending: boolean;
}

const initialState: ShoreDataApproverState = {
    error: null,
    shoreDataApproversData: null,
    shoreDataApproverUsersData: null,
    shoreDataApproverVesselGroupsData: null,

    isGetShoreDataApproversPending: false,
    isCreateShoreDataApproverPending: false,
    isUpdateShoreDataApproverPending: false,
    isDeleteShoreDataApproverPending: false,
    isGetShoreDataApproverUsersPending: false,
    isGetShoreDataApproverVesselGroupsPending: false,
};

export const getShoreDataApprovers = createAsyncThunk<
    DataApproverAccessSearchResponse,
    { tenantAlias: string; payload: DataApproverRequest },
    { rejectValue: ShoreDataApproverError }
>('shoreDataApprover/getShoreDataApprovers', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiDataApproverAccess(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createShoreDataApprover = createAsyncThunk<
    DataApproverAccessResponse,
    { tenantAlias: string; payload: UpsertDataApproverAccessRequest },
    { rejectValue: ShoreDataApproverError }
>('shoreDataApprover/createShoreDataApprover', ({ tenantAlias, payload }, { rejectWithValue }) =>
    createApiDataApproverAccess(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateShoreDataApprover = createAsyncThunk<
    DataApproverAccessResponse,
    { tenantAlias: string; accessId: number; payload: UpsertDataApproverAccessRequest },
    { rejectValue: ShoreDataApproverError }
>('shoreDataApprover/updateShoreDataApprover', ({ tenantAlias, accessId, payload }, { rejectWithValue }) =>
    updateApiDataApproverAccess(tenantAlias, accessId, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteShoreDataApprover = createAsyncThunk<
    void,
    { tenantAlias: string; accessId: number },
    { rejectValue: ShoreDataApproverError }
>('shoreDataApprover/deleteShoreDataApprover', ({ tenantAlias, accessId }, { rejectWithValue }) =>
    deleteApiDataApproverAccess(tenantAlias, accessId)
        .then(() => undefined)
        .catch((err) => rejectWithValue(err)),
);

export const getShoreDataApproverUsers = createAsyncThunk<
    DataApproverUsersSearchResponse,
    { tenantAlias: string; payload: DataApproverRequest },
    { rejectValue: ShoreDataApproverError }
>('shoreDataApprover/getShoreDataApproverUsers', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiDataApproverUsers(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getShoreDataApproverVesselGroups = createAsyncThunk<
    DataApproverVesselGroupsSearchResponse,
    { tenantAlias: string; payload: DataApproverRequest },
    { rejectValue: ShoreDataApproverError }
>('shoreDataApprover/getShoreDataApproverVesselGroups', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiDataApproverVesselGroups(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const shoreDataApproverSlice = createSlice({
    name: 'shoreDataApprover',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getShoreDataApprovers.pending, (state) => {
            state.isGetShoreDataApproversPending = true;
            state.error = null;
        });
        builder.addCase(getShoreDataApprovers.fulfilled, (state, action) => {
            state.isGetShoreDataApproversPending = false;
            state.shoreDataApproversData = action.payload;
        });
        builder.addCase(getShoreDataApprovers.rejected, (state, action) => {
            state.isGetShoreDataApproversPending = false;
            state.error = extractError(action);
        });

        builder.addCase(createShoreDataApprover.pending, (state) => {
            state.isCreateShoreDataApproverPending = true;
            state.error = null;
        });
        builder.addCase(createShoreDataApprover.fulfilled, (state) => {
            state.isCreateShoreDataApproverPending = false;
        });
        builder.addCase(createShoreDataApprover.rejected, (state, action) => {
            state.isCreateShoreDataApproverPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateShoreDataApprover.pending, (state) => {
            state.isUpdateShoreDataApproverPending = true;
            state.error = null;
        });
        builder.addCase(updateShoreDataApprover.fulfilled, (state) => {
            state.isUpdateShoreDataApproverPending = false;
        });
        builder.addCase(updateShoreDataApprover.rejected, (state, action) => {
            state.isUpdateShoreDataApproverPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteShoreDataApprover.pending, (state) => {
            state.isDeleteShoreDataApproverPending = true;
            state.error = null;
        });
        builder.addCase(deleteShoreDataApprover.fulfilled, (state) => {
            state.isDeleteShoreDataApproverPending = false;
        });
        builder.addCase(deleteShoreDataApprover.rejected, (state, action) => {
            state.isDeleteShoreDataApproverPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getShoreDataApproverUsers.pending, (state) => {
            state.isGetShoreDataApproverUsersPending = true;
            state.error = null;
        });
        builder.addCase(getShoreDataApproverUsers.fulfilled, (state, action) => {
            state.isGetShoreDataApproverUsersPending = false;
            state.shoreDataApproverUsersData = action.payload;
        });
        builder.addCase(getShoreDataApproverUsers.rejected, (state, action) => {
            state.isGetShoreDataApproverUsersPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getShoreDataApproverVesselGroups.pending, (state) => {
            state.isGetShoreDataApproverVesselGroupsPending = true;
            state.error = null;
        });
        builder.addCase(getShoreDataApproverVesselGroups.fulfilled, (state, action) => {
            state.isGetShoreDataApproverVesselGroupsPending = false;
            state.shoreDataApproverVesselGroupsData = action.payload;
        });
        builder.addCase(getShoreDataApproverVesselGroups.rejected, (state, action) => {
            state.isGetShoreDataApproverVesselGroupsPending = false;
            state.error = extractError(action);
        });
    },
});

export default shoreDataApproverSlice.reducer;
